/**
 * The small derivations behind the App metadata section on a device.
 *
 * No value imports, on the precedent `analytics-window.ts` and
 * `analytics-user-usage.ts` set: Node strips types, so a module with only type
 * imports and no JSX runs directly under `node` and the self-check at the foot
 * of this file IS its test suite.
 *
 * Everything here is about SAYING what arrived rather than interpreting it. A
 * metadata payload is a document a customer's own client wrote, stored
 * verbatim, and the console is the place it is most tempting to trust: none of
 * these functions reads a key out of one, and the only one that touches a
 * payload at all turns it into a string.
 */

/** Thousands separators, hand-rolled rather than `toLocaleString`, which
 *  answers differently depending on the box's locale — including the box the
 *  self-check below runs on. */
function group(value: number): string {
  const digits = String(value);
  let out = "";
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) {
      out += ",";
    }
    out += digits[i];
  }
  return out;
}

/**
 * A size, in the units an operator reads it in AND in the exact bytes.
 *
 * Both, because the two are used for different things in the same sentence:
 * "40.0 KiB" is what tells somebody the document is large, and "40,960 bytes"
 * is what they compare against the inline limit the response states. Rounding
 * alone would print a 32,769-byte payload and a 32,768-byte one identically —
 * and those two are on opposite sides of the rule that elided one of them.
 */
export function describeBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    // payload_bytes is NOT NULL and non-negative in 0038, so this is a wire
    // value that disagrees with the schema. Say so rather than printing
    // "-1 bytes" or "NaN KiB" as though it were a measurement.
    return "an unknown size";
  }
  const whole = Math.floor(bytes);
  if (whole < 1024) {
    return whole === 1 ? "1 byte" : `${group(whole)} bytes`;
  }
  const mib = whole >= 1024 * 1024;
  const scaled = mib ? whole / (1024 * 1024) : whole / 1024;
  return `${scaled.toFixed(1)} ${mib ? "MiB" : "KiB"} (${group(whole)} bytes)`;
}

/**
 * The count in the CLOSED disclosure, so "this installation has never reported"
 * is readable without a click — the rule DeviceKeys and DeviceApprovals already
 * follow on this card.
 *
 * `truncated` is the ENVELOPE's flag and not a comparison against a cap: the
 * server reports it even when false precisely so "fifty reports" and "at least
 * fifty reports" are not one answer, and re-deriving it here from a page size
 * would be a second opinion about the server's own list.
 */
export function metadataCount(rows: number, truncated: boolean): string {
  const counted = rows === 0 ? "no report" : rows === 1 ? "1 report" : `${group(rows)} reports`;
  return truncated ? `${counted} — the newest this page loads` : counted;
}

/**
 * `report_count` in words.
 *
 * The number is the difference between a document posted once during an
 * install and one posted every thirty seconds by a loop, which is a fact about
 * the client's behaviour rather than about the document — so it reads as a
 * sentence in the summary rather than as a bare integer beside four others.
 */
export function reportCountLabel(count: number): string {
  if (count === 1) {
    return "reported once";
  }
  if (count === 2) {
    return "reported twice";
  }
  return `reported ${group(count)} times`;
}

/**
 * Which of the four things the section can be saying.
 *
 * `unread` is a state and not a spinner, and that is the whole reason this is
 * an enumeration rather than a pair of booleans off the query: the read is not
 * issued until the disclosure is opened, and a react-query query that has never
 * been enabled reports `isPending` for ever. A spinner standing in for a
 * question nobody put never resolves.
 */
export type MetadataReadState = "unread" | "loading" | "failed" | "ready";

/**
 * ORDER MATTERS, and it is the order of what the operator can act on.
 *
 * `unread` first, because a closed section has no read to be pending. `failed`
 * before `ready`, because an error that arrived with a previous page still on
 * screen is a REFRESH that failed — the page is real and is shown, and the
 * failure is said above it rather than instead of it. Only an error with
 * nothing behind it is the whole answer.
 */
export function readState(open: boolean, failed: boolean, hasPage: boolean): MetadataReadState {
  if (!open) {
    return "unread";
  }
  if (failed) {
    return "failed";
  }
  return hasPage ? "ready" : "loading";
}

/** What the CLOSED disclosure says, so every state of the section — including
 *  "nobody has asked" — is legible without opening it. */
export function disclosureLabel(
  state: MetadataReadState,
  rows: number,
  truncated: boolean,
): string {
  switch (state) {
    case "unread":
      return "not read yet";
    case "loading":
      return "reading";
    case "failed":
      return "could not be read";
    default:
      return metadataCount(rows, truncated);
  }
}

/**
 * `{}` is a document a client genuinely sends — the inventory of a machine
 * that has none of the thing being inventoried — and it is NOT the same fact
 * as a device that has never reported at all. Both would draw as almost
 * nothing, so the section says which one it is looking at.
 */
export function payloadIsEmpty(payload: Record<string, unknown>): boolean {
  return Object.keys(payload).length === 0;
}

/**
 * The payload as TEXT, two-space indented.
 *
 * A string, and deliberately nothing richer: whatever comes back goes into a
 * `<pre>` as a text child, so React escapes it and no key in the document can
 * decide how it is drawn. A renderer that walked the object would be handing
 * an untrusted client authorship of the console's own markup.
 */
export function payloadText(payload: Record<string, unknown>): string {
  return JSON.stringify(payload, null, 2);
}

/**
 * Whether the report an operator asked to see whole is missing from the page
 * that came back.
 *
 * `?full=true` clamps the page from fifty to ten, so asking for whole
 * documents can push the very row the operator clicked off the end of the
 * list. Silently, and while showing them ten other reports with their payloads
 * open — which reads exactly like an answer.
 *
 * `full` is the RESPONSE's echoed flag, never the local request state: during
 * the refetch the previous (elided) page is still on screen, and asking the
 * question against a flag that has already moved would flash the warning at a
 * list the flag does not describe yet.
 */
export function missingFromFullPage(
  wanted: string | null,
  reports: readonly { id: string }[],
  full: boolean,
): boolean {
  if (!full || wanted === null) {
    return false;
  }
  return !reports.some((report) => report.id === wanted);
}

// ---------------------------------------------------------------------------
// Self-check — `node src/screens/device-metadata.ts`, wired into `make test-ts`
// ---------------------------------------------------------------------------

export function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `device-metadata self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  // ---- describeBytes ------------------------------------------------------
  check("describeBytes of nothing", describeBytes(0), "0 bytes");
  // Singular, because "1 bytes" is the kind of thing an operator reads as a
  // console that is not paying attention to anything else either.
  check("describeBytes of one byte", describeBytes(1), "1 byte");
  check("describeBytes below the unit", describeBytes(1023), "1,023 bytes");
  check("describeBytes at the unit", describeBytes(1024), "1.0 KiB (1,024 bytes)");
  // The elision boundary itself, both sides. These two are the whole reason
  // the exact count is printed beside the rounded one: to one decimal place
  // they are the same string.
  check("describeBytes at the inline limit", describeBytes(32768), "32.0 KiB (32,768 bytes)");
  check("describeBytes one over it", describeBytes(32769), "32.0 KiB (32,769 bytes)");
  check("describeBytes in MiB", describeBytes(1024 * 1024 + 512 * 1024), "1.5 MiB (1,572,864 bytes)");
  check("describeBytes groups four digits", describeBytes(40960), "40.0 KiB (40,960 bytes)");
  // A wire value that disagrees with 0038's schema is not a measurement.
  check("describeBytes of a negative", describeBytes(-1), "an unknown size");
  check("describeBytes of NaN", describeBytes(Number.NaN), "an unknown size");

  // ---- metadataCount ------------------------------------------------------
  check("metadataCount of an empty history", metadataCount(0, false), "no report");
  check("metadataCount of one", metadataCount(1, false), "1 report");
  check("metadataCount of several", metadataCount(5, false), "5 reports");
  check("metadataCount groups", metadataCount(1200, false), "1,200 reports");
  // The cut is the ENVELOPE's, and it is what stops the fiftieth report being
  // presented as the last one there is.
  check("metadataCount at the server's cut", metadataCount(50, true), "50 reports — the newest this page loads");
  check("metadataCount of one, cut", metadataCount(1, true), "1 report — the newest this page loads");

  // ---- reportCountLabel ---------------------------------------------------
  check("reportCountLabel of one", reportCountLabel(1), "reported once");
  check("reportCountLabel of two", reportCountLabel(2), "reported twice");
  check("reportCountLabel of many", reportCountLabel(3), "reported 3 times");
  check("reportCountLabel groups", reportCountLabel(86400), "reported 86,400 times");

  // ---- readState / disclosureLabel ----------------------------------------
  check("a closed section has asked nothing", readState(false, false, false), "unread");
  // Closed WINS over everything, including a page left in the cache by an
  // earlier open: what the summary reports is the state of the section.
  check("closed beats a cached page", readState(false, false, true), "unread");
  check("closed beats a failure", readState(false, true, true), "unread");
  check("open with nothing yet is loading", readState(true, false, false), "loading");
  check("open with a page is ready", readState(true, false, true), "ready");
  check("open with a failure and no page failed", readState(true, true, false), "failed");
  // A failure over a page that is still on screen is a refresh that failed.
  // Reporting the section as "could not be read" would deny the rows below it.
  check("a failed refresh over a page still reads", readState(true, false, true), "ready");
  check("the unread label", disclosureLabel("unread", 0, false), "not read yet");
  check("the loading label", disclosureLabel("loading", 0, false), "reading");
  check("the failed label", disclosureLabel("failed", 0, false), "could not be read");
  check("the ready label counts", disclosureLabel("ready", 3, false), "3 reports");
  check("the ready label carries the cut", disclosureLabel("ready", 50, true), "50 reports — the newest this page loads");
  // An empty history is readable from the closed summary — the whole point of
  // putting the count there.
  check("the ready label of an empty history", disclosureLabel("ready", 0, false), "no report");

  // ---- payloadIsEmpty / payloadText ---------------------------------------
  check("payloadIsEmpty of {}", payloadIsEmpty({}), true);
  // A key whose VALUE is empty is not an empty document — the client said
  // something, and what it said was "none".
  check("payloadIsEmpty of a document with a key", payloadIsEmpty({ plugins: [] }), false);
  check("payloadText indents", payloadText({ a: 1 }), '{\n  "a": 1\n}');
  check("payloadText of {}", payloadText({}), "{}");
  // The point of the whole file: a payload that is trying to be markup comes
  // back as the CHARACTERS of that markup, and React escapes them from there.
  check(
    "payloadText leaves markup as text",
    payloadText({ hostname: "<img src=x onerror=alert(1)>" }),
    '{\n  "hostname": "<img src=x onerror=alert(1)>"\n}',
  );

  // ---- missingFromFullPage ------------------------------------------------
  const page = [{ id: "a" }, { id: "b" }];
  check("nothing was asked for", missingFromFullPage(null, page, true), false);
  check("the asked-for row is present", missingFromFullPage("a", page, true), false);
  check("the asked-for row fell off the page", missingFromFullPage("z", page, true), true);
  // The elided page is not evidence of anything: it is the page the operator
  // asked to replace, and it is on screen for as long as the refetch runs.
  check("an elided page cannot be missing a full row", missingFromFullPage("z", page, false), false);
  check("an empty full page is missing it", missingFromFullPage("z", [], true), true);

  console.log(`device-metadata self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
